import React, { FunctionComponent } from 'react';
import { map, addIndex, isNil } from 'ramda';
import withStyles from 'react-jss';
import { Grid, Image } from 'semantic-ui-react';
import { PieChart, Pie, Cell } from 'recharts';

import Legend from 'components/Legend';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import { PieLabelProps } from 'components/LapsedActivity/LapsedActivity.helpers';
import { Segment } from 'components/Charts/Demographics/Demographics.helper';
import NoDataImage from 'assets/icons/NoData/gender.svg';

import SectionHeader from '../Header';

type PieCartColors = {
  [key: string]: string;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  genderGroup: Segment[];
  title?: string;
  width?: number;
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
};

const { seafoamGreen, violet } = theme;

const RATIO_CHART_LEGEND = [
  { title: 'Women', type: 'female', color: violet },
  { title: 'Men', type: 'male', color: seafoamGreen },
];

const pieCartColors: PieCartColors = {
  female: violet,
  male: seafoamGreen,
};

const styles = {
  radialChart: {
    padding: '0 !important',
    marginTop: '2rem',
    justifyContent: 'center !important',
  },
};

const mapIndexed = addIndex<any>(map);

const getPoint = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
}: PieLabelProps) => {
  const RADIAN = Math.PI / 180;
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return { x, y };
};

const renderCustomizedLabel = ({ cx, percent, index, ...rest }: any) => {
  const { x, y } = getPoint({ ...rest, cx });

  return (
    <text
      x={x}
      y={y}
      fill={'rgba(74,74,74, .8)'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${Math.round(percent)}%`}
    </text>
  );
};

const renderCells = mapIndexed(({ segmentLabel }: any, index: number) => (
  <Cell key={`cell-${index}`} fill={pieCartColors[segmentLabel]} />
));

const Gender: FunctionComponent<Props> = ({
  classes,
  genderGroup,
  title,
  width = 250,
  height = 300,
  innerRadius,
  outerRadius,
}) => (
  <Grid columns="1">
    {title && (
      <Grid.Row>
        <SectionHeader title={title} />
      </Grid.Row>
    )}
    {isNil(genderGroup) ? (
      <Image src={NoDataImage} />
    ) : (
      <Grid.Row className={classes.radialChart}>
        <PieChart width={width} height={height}>
          <Pie
            data={genderGroup}
            label={renderCustomizedLabel}
            labelLine
            isAnimationActive={false}
            nameKey="segmentLabel"
            dataKey="percent"
            startAngle={90}
            endAngle={-270}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
          >
            {renderCells(genderGroup as any)}
          </Pie>
        </PieChart>
      </Grid.Row>
    )}
    <Grid.Row>
      <Legend legend={RATIO_CHART_LEGEND} />
    </Grid.Row>
  </Grid>
);

export default withStyles(styles)(Gender);
